import { matchKeywords } from './keywords';
import { FAULTLINES } from './rules';
import type {
  FaultlineDefinition,
  FaultlineResult,
  FaultlinesInput,
  FaultlinesOutput,
  TrustBoundary,
} from './types';

const KEYWORD_POINTS = 4;

function clampScore(score: number) {
  return Math.max(0, Math.min(100, Math.round(score)));
}

function applyRules(
  def: FaultlineDefinition,
  input: FaultlinesInput,
  keywords: ReturnType<typeof matchKeywords>
) {
  let score = def.baseScore;
  for (const rule of def.rules) {
    if (rule.when(input, keywords)) {
      score += rule.points;
    }
  }
  const keywordHits = keywords.hits[def.id]?.length ?? 0;
  score += keywordHits * KEYWORD_POINTS;
  return { score: clampScore(score), keywords };
}

function buildFaultlineResult(def: FaultlineDefinition, score: number): FaultlineResult {
  const active = score >= def.threshold;
  return {
    id: def.id,
    title: def.title,
    score,
    signals: active ? def.signals : [],
    moves: active ? def.moves : [],
    failureMode: active
      ? def.failureModes[0]
      : 'Keep an eye on this as the system grows.',
    nextStep: active
      ? def.nextSteps[0]
      : 'Document current assumptions and revisit after the next milestone.',
  };
}

function computeConcentration(scores: number[]): 'Low' | 'Medium' | 'High' {
  const maxScore = Math.max(...scores);
  const highCount = scores.filter((score) => score >= 55).length;
  if (maxScore >= 70 || highCount >= 3) {
    return 'High';
  }
  if (maxScore >= 40) {
    return 'Medium';
  }
  return 'Low';
}

function buildWhy(input: FaultlinesInput) {
  const reasons: string[] = [];
  if (input.exposure !== 'internal') {
    reasons.push('Public exposure increases edge and identity risk.');
  }
  if (input.multiTenancy === 'multi') {
    reasons.push('Multi-tenant boundaries elevate isolation and data leak risk.');
  }
  if (input.changeRate === 'high') {
    reasons.push(
      'High change velocity amplifies misconfiguration and supply chain risk.'
    );
  }
  if (input.dataSensitivity === 'high') {
    reasons.push('High-sensitivity data raises the impact of exfiltration paths.');
  }
  if (input.observability === 'minimal') {
    reasons.push('Weak observability makes detection and response slower.');
  }
  if (reasons.length === 0) {
    return 'Your inputs point to a lower concentration of obvious risk, but hidden coupling can still emerge.';
  }
  return reasons.slice(0, 2).join(' ');
}

function buildBoundaries(input: FaultlinesInput): TrustBoundary[] {
  const boundaries: TrustBoundary[] = [
    { id: 'users', label: 'Users', active: true },
    {
      id: 'edge',
      label: 'Edge',
      active: input.exposure !== 'internal' || input.deployment === 'edge',
    },
    {
      id: 'app',
      label: 'App',
      active: input.systemType !== 'iot' && input.systemType !== 'agent',
    },
    {
      id: 'data',
      label: 'Data',
      active: input.dataSensitivity !== 'low',
    },
    {
      id: 'admin',
      label: 'Admin plane',
      active: input.privilegedAccess !== 'jit' || input.identity !== 'mtls',
    },
  ];
  return boundaries;
}

function buildQuestions(faultlines: FaultlineResult[], threshold: number) {
  const active = faultlines.filter((line) => line.score >= threshold);
  const selected = active.length > 0 ? active : faultlines.slice(0, 3);
  const questions = selected
    .flatMap((line) => {
      const def = FAULTLINES.find((item) => item.id === line.id);
      return def ? def.questions : [];
    })
    .filter((value, index, self) => self.indexOf(value) === index);
  return questions.slice(0, 10);
}

export function analyzeFaultlines(input: FaultlinesInput): FaultlinesOutput {
  const keywords = matchKeywords(input.notes);
  const scored = FAULTLINES.map((def) => {
    const result = applyRules(def, input, keywords);
    return buildFaultlineResult(def, result.score);
  }).sort((a, b) => b.score - a.score);

  const scores = scored.map((item) => item.score);
  const concentration = computeConcentration(scores);
  const threshold = 35;
  const topCandidates = scored.filter((line) => line.score >= threshold);
  const topFaultlines =
    topCandidates.length >= 3 ? topCandidates.slice(0, 6) : scored.slice(0, 3);

  return {
    summary: {
      topFaultlines,
      why: buildWhy(input),
      concentration,
    },
    boundaries: buildBoundaries(input),
    faultlines: scored,
    questions: buildQuestions(scored, threshold),
  };
}

export function buildMarkdown(output: FaultlinesOutput) {
  const lines: string[] = [];
  lines.push('# Faultlines Summary');
  lines.push('');
  lines.push(`Risk concentration: ${output.summary.concentration}`);
  lines.push('');
  lines.push('Top faultlines:');
  output.summary.topFaultlines.forEach((line) => {
    lines.push(`- ${line.title} (${line.score})`);
  });
  lines.push('');
  lines.push(output.summary.why);
  lines.push('');
  lines.push('## Faultlines');
  for (const line of output.faultlines) {
    lines.push(`### ${line.title} (${line.score})`);
    if (line.signals.length > 0) {
      lines.push('Signals:');
      line.signals.forEach((signal) => lines.push(`- ${signal}`));
    }
    if (line.moves.length > 0) {
      lines.push('First hardening moves:');
      line.moves.forEach((move) => lines.push(`- ${move}`));
    }
    lines.push(`Common failure mode: ${line.failureMode}`);
    lines.push(`Good enough next step: ${line.nextStep}`);
    lines.push('');
  }
  lines.push('## Questions to ask next');
  output.questions.forEach((question) => lines.push(`- ${question}`));
  return lines.join('\n');
}

export function buildShareSummary(output: FaultlinesOutput) {
  const top = output.summary.topFaultlines.map((line) => line.title).join(', ');
  return [
    'Faultlines summary',
    `Risk concentration: ${output.summary.concentration}`,
    `Top faultlines: ${top}`,
    output.summary.why,
  ].join('\n');
}
